import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import FileUploader from 'components/FileUploader'
import Spinner from 'components/Spinner'
import Button from 'components/Button'
import { ImageIcon } from 'lucide-react'

interface ImagePickerPopupProps {
	placeId?: string | null
	searchQuery?: string | null
	locationName: string
	onImageSelected: (imageUrl: string) => void
}

const ImagePickerPopup = ({ placeId, searchQuery, locationName, onImageSelected }: ImagePickerPopupProps) => {
	const [photos, setPhotos] = useState<string[]>([])
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [isLoadingPhotos, setIsLoadingPhotos] = useState(false)
	const [isUploading, setIsUploading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const placesRef = useRef<HTMLDivElement>(null)

	const extractPhotos = (result: any) => {
		if (!result?.photos || result.photos.length === 0) {
			setError('No photos found for this location.')
			return
		}
		const urls = result.photos
			.slice(0, 6)
			.map((photo: any) => photo.getUrl({ maxWidth: 800 }))
		setPhotos(urls)
	}

	useEffect(() => {
		if ((!placeId && !searchQuery) || !placesRef.current) return

		setIsLoadingPhotos(true)
		setError(null)
		setPhotos([])
		setSelectedIndex(0)

		try {
			const service = new (window as any).google.maps.places.PlacesService(placesRef.current)
			const OK = (window as any).google.maps.places.PlacesServiceStatus.OK

			if (placeId) {
				service.getDetails(
					{ placeId, fields: ['photos'] },
					(result: any, status: any) => {
						setIsLoadingPhotos(false)
						if (status !== OK) {
							setError('Could not load photos for this location.')
							return
						}
						extractPhotos(result)
					},
				)
			} else if (searchQuery) {
				service.textSearch(
					{ query: searchQuery },
					(results: any[], status: any) => {
						if (status !== OK || !results || results.length === 0) {
							setIsLoadingPhotos(false)
							setError('Could not find photos for this location.')
							return
						}
						const foundPlaceId = results[0].place_id
						if (!foundPlaceId) {
							setIsLoadingPhotos(false)
							extractPhotos(results[0])
							return
						}
						service.getDetails(
							{ placeId: foundPlaceId, fields: ['photos'] },
							(detailResult: any, detailStatus: any) => {
								setIsLoadingPhotos(false)
								if (detailStatus !== OK) {
									extractPhotos(results[0])
									return
								}
								extractPhotos(detailResult)
							},
						)
					},
				)
			}
		} catch (err) {
			setIsLoadingPhotos(false)
			setError('Failed to fetch location photos.')
		}
	}, [placeId, searchQuery])

	const handlePrev = () => {
		setSelectedIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1))
	}

	const handleNext = () => {
		setSelectedIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1))
	}

	const handleSelectGooglePhoto = async () => {
		setIsUploading(true)
		setError(null)
		try {
			const result = await uploadToCloudinary(photos[selectedIndex])
			onImageSelected(result.secure_url)
		} catch (err) {
			console.error('[ImagePickerPopup] Failed to upload photo', err)
			setError('Failed to upload the selected photo. Please try again or upload from your device.')
			setIsUploading(false)
		}
	}

	const handleLocalUpload = (info: any) => {
		onImageSelected(info.cdnUrl || info.originalUrl)
	}

	if (!placeId && !searchQuery) {
		return (
			<div className="flex flex-col items-center gap-4 py-4">
				<p className="text-sm text-gray-500 italic">Please select a location first to search for photos.</p>
				<FileUploader onChange={handleLocalUpload}>
					<ImageIcon className="w-4 h-4" /> Upload From My Device
				</FileUploader>
			</div>
		)
	}

	return (
		<div className="flex flex-col items-center gap-4 py-2">
			<div ref={placesRef} className="hidden" />

			{isLoadingPhotos && (
				<div className="flex flex-col items-center gap-2 py-8">
					<Spinner />
					<p className="text-sm text-gray-500">Searching for photos...</p>
				</div>
			)}

			{!isLoadingPhotos && photos.length > 0 && (
				<>
					{/* Main preview with navigation arrows */}
					<div className="relative flex items-center justify-center w-full">
						<button
							type="button"
							className="absolute left-0 z-[1] inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
							onClick={handlePrev}
							aria-label="Previous photo"
						>
							<ChevronLeft className="w-5 h-5" />
						</button>

						<div className="flex items-center justify-center w-full min-h-[300px]">
							{isUploading ? (
								<div className="flex flex-col items-center gap-2">
									<Spinner />
									<p className="text-sm text-gray-500">Uploading to cloud...</p>
								</div>
							) : (
								<img
									src={photos[selectedIndex]}
									alt={`${locationName} - photo ${selectedIndex + 1}`}
									className="max-h-[400px] max-w-full object-contain rounded-lg"
								/>
							)}
						</div>

						<button
							type="button"
							className="absolute right-0 z-[1] inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
							onClick={handleNext}
							aria-label="Next photo"
						>
							<ChevronRight className="w-5 h-5" />
						</button>
					</div>

					{/* Thumbnail strip */}
					<div className="flex gap-2 justify-center flex-wrap">
						{photos.map((url, index) => (
							<img
								key={index}
								src={url}
								alt={`Thumbnail ${index + 1}`}
								className={`w-16 h-16 object-cover rounded cursor-pointer transition-all ${
									index === selectedIndex
										? 'ring-2 ring-blue-500 opacity-100'
										: 'opacity-60 hover:opacity-100'
								}`}
								onClick={() => setSelectedIndex(index)}
							/>
						))}
					</div>

					{/* Select button */}
					<Button
						variant="primary"
						onClick={handleSelectGooglePhoto}
						disabled={isUploading}
					>
						Select This Photo
					</Button>

					{/* Divider */}
					<div className="flex items-center w-full gap-3 px-4">
						<hr className="flex-1 border-gray-300" />
						<span className="text-sm text-gray-400">or</span>
						<hr className="flex-1 border-gray-300" />
					</div>
				</>
			)}

			{!isLoadingPhotos && error && (
				<p className="text-sm text-gray-500 italic">{error}</p>
			)}

			{/* Local upload fallback - always visible when not loading */}
			{!isLoadingPhotos && (
				<FileUploader onChange={handleLocalUpload}>
					<ImageIcon className="w-4 h-4" /> Upload From My Device
				</FileUploader>
			)}
		</div>
	)
}

export default ImagePickerPopup
